import type { RideAnalytics, RideRecord } from "../types/ride";

const WARMUP_EXCLUDED_SECONDS = 600;
const MIN_VALID_DURATION_SECONDS = 1200;
const MIN_COVERAGE_PERCENT = 80;
const LONG_GAP_MULTIPLIER = 5;

type AerobicDecouplingResult = RideAnalytics["aerobicDecoupling"];

type Measurement = {
  value: number | null;
  invalid: boolean;
};

type Segment = {
  startSeconds: number;
  endSeconds: number;
  powerWatts: number | null;
  heartRateBpm: number | null;
  isGap: boolean;
  invalidData: boolean;
};

function readMeasurement(value: unknown): Measurement {
  if (value === null || value === undefined || value === "") {
    return { value: null, invalid: false };
  }

  if (typeof value === "number") {
    if (!Number.isFinite(value)) {
      return { value: null, invalid: true };
    }

    return { value, invalid: false };
  }

  return { value: null, invalid: false };
}

function toRecordTimeSeconds(record: RideRecord): Measurement {
  const elapsedSeconds = readMeasurement(record.elapsedSeconds);
  if (elapsedSeconds.invalid || elapsedSeconds.value !== null) {
    return elapsedSeconds;
  }

  if (record.timestamp) {
    const parsed = Date.parse(record.timestamp);
    if (!Number.isNaN(parsed)) {
      return { value: parsed / 1000, invalid: false };
    }
  }

  return { value: null, invalid: false };
}

function getMedian(values: number[]): number | null {
  if (values.length === 0) {
    return null;
  }

  const sortedValues = [...values].sort((left, right) => left - right);
  const middleIndex = Math.floor(sortedValues.length / 2);
  if (sortedValues.length % 2 === 1) {
    return sortedValues[middleIndex];
  }

  return (sortedValues[middleIndex - 1] + sortedValues[middleIndex]) / 2;
}

function formatUnavailable(
  status: AerobicDecouplingResult["status"],
  validDurationSeconds: number,
  validSampleCount: number,
  coveragePercent: number | null
): AerobicDecouplingResult {
  return {
    status,
    interpretation: "Not enough valid data",
    decouplingPercent: null,
    firstHalfAveragePower: null,
    firstHalfAverageHeartRate: null,
    firstHalfEfficiencyFactor: null,
    secondHalfAveragePower: null,
    secondHalfAverageHeartRate: null,
    secondHalfEfficiencyFactor: null,
    validDurationSeconds,
    validSampleCount,
    coveragePercent,
    warmupExcludedSeconds: WARMUP_EXCLUDED_SECONDS
  };
}

function interpretDecoupling(decouplingPercent: number): string {
  if (decouplingPercent < 0) {
    return "Improved efficiency / possible warm-up effect";
  }

  if (decouplingPercent < 3) {
    return "Excellent";
  }

  if (decouplingPercent < 5) {
    return "Good";
  }

  if (decouplingPercent < 7.5) {
    return "Moderate drift";
  }

  return "High drift";
}

function computeWeightedAverage(segments: Segment[], startSeconds: number, endSeconds: number, selector: (segment: Segment) => number): number | null {
  let weightedSum = 0;
  let totalDuration = 0;

  for (const segment of segments) {
    const overlapStart = Math.max(segment.startSeconds, startSeconds);
    const overlapEnd = Math.min(segment.endSeconds, endSeconds);
    const overlapDuration = overlapEnd - overlapStart;

    if (overlapDuration <= 0) {
      continue;
    }

    weightedSum += selector(segment) * overlapDuration;
    totalDuration += overlapDuration;
  }

  if (totalDuration <= 0) {
    return null;
  }

  return weightedSum / totalDuration;
}

function buildSegments(records: RideRecord[], recordingIntervalSeconds: number | null | undefined): { segments: Segment[]; invalidData: boolean; nominalIntervalSeconds: number | null } {
  let invalidData = false;

  const samples = records
    .map(record => {
      const time = toRecordTimeSeconds(record);
      const power = readMeasurement(record.powerWatts);
      const heartRate = readMeasurement(record.heartRateBpm);

      if (time.invalid || power.invalid || heartRate.invalid) {
        invalidData = true;
      }

      return {
        timeSeconds: time.value,
        powerWatts: power.value,
        heartRateBpm: heartRate.value
      };
    })
    .filter(sample => sample.timeSeconds !== null)
    .map(sample => ({
      timeSeconds: sample.timeSeconds as number,
      powerWatts: sample.powerWatts,
      heartRateBpm: sample.heartRateBpm
    }))
    .sort((left, right) => left.timeSeconds - right.timeSeconds);

  if (samples.length < 2) {
    return { segments: [], invalidData, nominalIntervalSeconds: null };
  }

  const positiveIntervals = samples
    .slice(1)
    .map((sample, index) => sample.timeSeconds - samples[index].timeSeconds)
    .filter(interval => interval > 0);

  const inferredIntervalSeconds = recordingIntervalSeconds && Number.isFinite(recordingIntervalSeconds) && recordingIntervalSeconds > 0
    ? recordingIntervalSeconds
    : getMedian(positiveIntervals);

  if (inferredIntervalSeconds === null || inferredIntervalSeconds <= 0) {
    return { segments: [], invalidData: true, nominalIntervalSeconds: null };
  }

  const gapThresholdSeconds = inferredIntervalSeconds * LONG_GAP_MULTIPLIER;
  const segments: Segment[] = [];

  for (let index = 0; index < samples.length; index += 1) {
    const current = samples[index];
    const next = samples[index + 1];
    const startSeconds = current.timeSeconds;
    const endSeconds = next ? next.timeSeconds : current.timeSeconds + inferredIntervalSeconds;
    const durationSeconds = endSeconds - startSeconds;

    if (durationSeconds <= 0) {
      continue;
    }

    const isGap = next !== undefined && durationSeconds > gapThresholdSeconds;
    segments.push({
      startSeconds,
      endSeconds,
      powerWatts: current.powerWatts,
      heartRateBpm: current.heartRateBpm,
      isGap,
      invalidData: isGap
    });
  }

  return { segments, invalidData, nominalIntervalSeconds: inferredIntervalSeconds };
}

function clipSegments(segments: Segment[], windowStartSeconds: number, windowEndSeconds: number): Segment[] {
  return segments
    .map(segment => ({
      ...segment,
      startSeconds: Math.max(segment.startSeconds, windowStartSeconds),
      endSeconds: Math.min(segment.endSeconds, windowEndSeconds)
    }))
    .filter(segment => segment.endSeconds > segment.startSeconds);
}

function coverageDuration(segments: Segment[], startSeconds: number, endSeconds: number, predicate: (segment: Segment) => boolean): number {
  let total = 0;

  for (const segment of segments) {
    if (!predicate(segment)) {
      continue;
    }

    const overlapStart = Math.max(segment.startSeconds, startSeconds);
    const overlapEnd = Math.min(segment.endSeconds, endSeconds);
    const overlapDuration = overlapEnd - overlapStart;
    if (overlapDuration > 0) {
      total += overlapDuration;
    }
  }

  return total;
}

export function computeAerobicDecoupling(records: RideRecord[], recordingIntervalSeconds: number | null | undefined = null): AerobicDecouplingResult {
  const { segments, invalidData, nominalIntervalSeconds } = buildSegments(records, recordingIntervalSeconds);

  if (invalidData) {
    return formatUnavailable("invalid-data", 0, 0, null);
  }

  if (nominalIntervalSeconds === null || segments.length === 0) {
    return formatUnavailable("insufficient-duration", 0, 0, null);
  }

  const totalAnalysisStartSeconds = WARMUP_EXCLUDED_SECONDS;
  const totalAnalysisEndSeconds = segments[segments.length - 1].endSeconds;
  const totalAnalysisDurationSeconds = totalAnalysisEndSeconds - totalAnalysisStartSeconds;
  if (totalAnalysisDurationSeconds <= 0) {
    return formatUnavailable("insufficient-duration", 0, 0, null);
  }

  const clippedSegments = clipSegments(segments, totalAnalysisStartSeconds, totalAnalysisEndSeconds);
  const validOverlapSegments = clippedSegments.filter(segment => !segment.isGap && segment.powerWatts !== null && segment.powerWatts > 0 && segment.heartRateBpm !== null && segment.heartRateBpm > 0);

  const validDurationSeconds = validOverlapSegments.reduce((sum, segment) => sum + (segment.endSeconds - segment.startSeconds), 0);
  const validSampleCount = validOverlapSegments.length;
  const coveragePercent = (validDurationSeconds / totalAnalysisDurationSeconds) * 100;
  const powerCoverageSeconds = clippedSegments.reduce((sum, segment) => {
    if (segment.isGap || segment.powerWatts === null || segment.powerWatts <= 0) {
      return sum;
    }

    return sum + (segment.endSeconds - segment.startSeconds);
  }, 0);
  const heartRateCoverageSeconds = clippedSegments.reduce((sum, segment) => {
    if (segment.isGap || segment.heartRateBpm === null || segment.heartRateBpm <= 0) {
      return sum;
    }

    return sum + (segment.endSeconds - segment.startSeconds);
  }, 0);
  const powerCoveragePercent = (powerCoverageSeconds / totalAnalysisDurationSeconds) * 100;
  const heartRateCoveragePercent = (heartRateCoverageSeconds / totalAnalysisDurationSeconds) * 100;

  if (validDurationSeconds < MIN_VALID_DURATION_SECONDS) {
    return formatUnavailable("insufficient-duration", validDurationSeconds, validSampleCount, coveragePercent);
  }

  if (powerCoveragePercent < MIN_COVERAGE_PERCENT && heartRateCoveragePercent >= MIN_COVERAGE_PERCENT) {
    return formatUnavailable("insufficient-power", validDurationSeconds, validSampleCount, coveragePercent);
  }

  if (heartRateCoveragePercent < MIN_COVERAGE_PERCENT && powerCoveragePercent >= MIN_COVERAGE_PERCENT) {
    return formatUnavailable("insufficient-heart-rate", validDurationSeconds, validSampleCount, coveragePercent);
  }

  if (coveragePercent < MIN_COVERAGE_PERCENT) {
    return formatUnavailable("insufficient-coverage", validDurationSeconds, validSampleCount, coveragePercent);
  }

  const midpointSeconds = totalAnalysisStartSeconds + totalAnalysisDurationSeconds / 2;
  const firstHalfSegments = validOverlapSegments.filter(segment => segment.startSeconds < midpointSeconds);
  const secondHalfSegments = validOverlapSegments.filter(segment => segment.endSeconds > midpointSeconds);

  const firstHalfPowerCoverage = coverageDuration(clippedSegments, totalAnalysisStartSeconds, midpointSeconds, segment => segment.powerWatts !== null && segment.powerWatts > 0);
  const firstHalfHeartRateCoverage = coverageDuration(clippedSegments, totalAnalysisStartSeconds, midpointSeconds, segment => segment.heartRateBpm !== null && segment.heartRateBpm > 0);
  const secondHalfPowerCoverage = coverageDuration(clippedSegments, midpointSeconds, totalAnalysisEndSeconds, segment => segment.powerWatts !== null && segment.powerWatts > 0);
  const secondHalfHeartRateCoverage = coverageDuration(clippedSegments, midpointSeconds, totalAnalysisEndSeconds, segment => segment.heartRateBpm !== null && segment.heartRateBpm > 0);

  if (firstHalfPowerCoverage <= 0 || secondHalfPowerCoverage <= 0) {
    return formatUnavailable("insufficient-power", validDurationSeconds, validSampleCount, coveragePercent);
  }

  if (firstHalfHeartRateCoverage <= 0 || secondHalfHeartRateCoverage <= 0) {
    return formatUnavailable("insufficient-heart-rate", validDurationSeconds, validSampleCount, coveragePercent);
  }

  const firstHalfAveragePower = computeWeightedAverage(firstHalfSegments, totalAnalysisStartSeconds, midpointSeconds, segment => segment.powerWatts as number);
  const firstHalfAverageHeartRate = computeWeightedAverage(firstHalfSegments, totalAnalysisStartSeconds, midpointSeconds, segment => segment.heartRateBpm as number);
  const secondHalfAveragePower = computeWeightedAverage(secondHalfSegments, midpointSeconds, totalAnalysisEndSeconds, segment => segment.powerWatts as number);
  const secondHalfAverageHeartRate = computeWeightedAverage(secondHalfSegments, midpointSeconds, totalAnalysisEndSeconds, segment => segment.heartRateBpm as number);

  if (
    firstHalfAveragePower === null ||
    firstHalfAverageHeartRate === null ||
    secondHalfAveragePower === null ||
    secondHalfAverageHeartRate === null ||
    firstHalfAverageHeartRate <= 0 ||
    secondHalfAverageHeartRate <= 0
  ) {
    return formatUnavailable("invalid-data", validDurationSeconds, validSampleCount, coveragePercent);
  }

  const firstHalfEfficiencyFactor = firstHalfAveragePower / firstHalfAverageHeartRate;
  const secondHalfEfficiencyFactor = secondHalfAveragePower / secondHalfAverageHeartRate;

  if (!Number.isFinite(firstHalfEfficiencyFactor) || !Number.isFinite(secondHalfEfficiencyFactor) || firstHalfEfficiencyFactor <= 0 || secondHalfEfficiencyFactor <= 0) {
    return formatUnavailable("invalid-data", validDurationSeconds, validSampleCount, coveragePercent);
  }

  const decouplingPercent = ((firstHalfEfficiencyFactor - secondHalfEfficiencyFactor) / firstHalfEfficiencyFactor) * 100;

  return {
    status: "available",
    interpretation: interpretDecoupling(decouplingPercent),
    decouplingPercent,
    firstHalfAveragePower,
    firstHalfAverageHeartRate,
    firstHalfEfficiencyFactor,
    secondHalfAveragePower,
    secondHalfAverageHeartRate,
    secondHalfEfficiencyFactor,
    validDurationSeconds,
    validSampleCount,
    coveragePercent,
    warmupExcludedSeconds: WARMUP_EXCLUDED_SECONDS
  };
}
